import React from "react";
import BreadCrumbs from "../common/BreadCrumbs";

const PrivacyPolicy = () => {
  return (
    <>
      <BreadCrumbs title="Privacy Policy" description="Our Privacy Policy" />
      <div className="container my-5 privacy-policy-area">
        <p>
          At <strong>AppThing Technologies</strong>, your privacy is our
          priority. We are deeply committed to safeguarding the personal
          information you share with us across our website,
          <a href="https://www.appthing.in"> https://www.appthing.in</a>, as
          well as any other digital platforms we own and manage. This Privacy
          Policy, effective from <strong>September 1, 2024</strong>, underscores
          our unwavering dedication to transparency, trust, and security.
        </p>

        <p>
          Through this policy, we aim to provide you with clear insights into
          the types of information we collect, how that information is used, and
          the measures we take to ensure its protection. Whether you are
          exploring our website or using our services, you can feel confident
          knowing your data is handled responsibly.
        </p>

        <p>
          If you have any questions or concerns about this Privacy Policy,
          contact us at <a href="mailto:info@appthing.in">info@appthing.in</a>.
          By using our Website and Services, you agree that your personal
          information will be processed in line with this Policy and the{" "}
          <strong>AppThing Technologies Terms of Use</strong>.
        </p>

        <br />
        <h3 className="my-3">A. Collection of Your Personal Information</h3>
        <h4>1. Collection of Personal Information</h4>
        <p>
          The term <strong>“personal information”</strong> refers to data that
          can be used to identify or contact you. This may include your name,
          address, email address, phone number, and similar details. The exact
          information collected depends on how you interact with our Website and
          may include:
        </p>
        <ul style={{ listStyle: "disc", paddingLeft: "20px" }}>
          <li>Full name, gender, age, and postal address</li>
          <li>Phone number and email address</li>
          <li>Identification proof and employment/education details</li>
          <li>Financial details such as bank, credit, or debit card info</li>
          <li>Additional data from third parties where legally permitted</li>
        </ul>

        <p>
          You agree to provide accurate and up-to-date information. We will
          allow you to correct inaccuracies or request deletion of your data,
          subject to legal requirements. However, requests may be denied if they
          conflict with obligations under law or affect others’ privacy.
        </p>

        <br />
        <h4>2. Collection from Social Accounts</h4>
        <p>
          If you sign in using social media accounts, we may collect details
          such as your name, profile photo, gender, user ID, date of birth, and
          email. Please note that the handling of your data by those platforms
          is governed by their own privacy policies.
        </p>

        <br />
        <h4>3. Automatic Data Collection, Cookies & Tracking</h4>
        <p>
          We and our partners use cookies and tracking tools to understand your
          interactions with our Website. These may include:
        </p>
        <ul style={{ listStyle: "disc", paddingLeft: "20px" }}>
          <li>Browser type, OS, IP address, and device details</li>
          <li>Pages visited and interaction frequency</li>
          <li>Clickstream data for analytics and improvements</li>
        </ul>
        <p>
          Cookies help remember your preferences. You can manage cookies through
          your browser settings, but disabling them may reduce site
          functionality.
        </p>

        <br />
        <h3>B. Security</h3>
        <p>We safeguard your personal data using:</p>
        <ul style={{ listStyle: "disc", paddingLeft: "20px" }}>
          <li>Encryption for sensitive data</li>
          <li>Password protection and secure authentication</li>
          <li>Physical security for servers and systems</li>
        </ul>
        <p>
          While we apply strong protections, no system is 100% secure. We
          encourage you to take precautions:
        </p>
        <ul style={{ listStyle: "circle", paddingLeft: "40px" }}>
          <li>Use strong and unique passwords</li>
          <li>Log out on shared devices</li>
          <li>Keep login details confidential</li>
        </ul>

        <br />
        <h3>C. Disclosure of Information</h3>
        <p>
          We may share your personal information under the following
          circumstances:
        </p>
        <ol style={{ listStyle: "decimal", paddingLeft: "20px" }}>
          <li>
            <strong>To Protect Us and Others:</strong> Disclosure may be
            required to:
            <ul style={{ listStyle: "square", paddingLeft: "40px" }}>
              <li>Prevent fraud or illegal activities</li>
              <li>Enforce this Privacy Policy and Terms of Use</li>
              <li>Protect our rights, property, or the safety of users</li>
            </ul>
          </li>
          <li>
            <strong>With Service Providers:</strong> We may share data with
            trusted vendors (e.g., payment processors, analytics partners) who
            use it only as needed to provide services for us.
          </li>
          <li>
            <strong>With Other Users:</strong> Content you post (e.g., feedback)
            may be visible to others.
          </li>
          <li>
            <strong>To Authorities:</strong> If required by law, we may share
            information with government or enforcement agencies.
          </li>
          <li>
            <strong>Business Changes:</strong> In case of merger or acquisition,
            your data may be transferred to a successor entity.
          </li>
          <li>
            <strong>At Your Request:</strong> We may disclose data if you
            explicitly consent.
          </li>
        </ol>

        <br />
        <h3>D. Links to External Websites</h3>
        <ul style={{ listStyle: "disc", paddingLeft: "20px" }}>
          <li>We do not control or endorse external websites.</li>
          <li>
            We are not responsible for their content, privacy practices, or
            security.
          </li>
          <li>
            Any information you provide to external sites is governed by their
            own policies.
          </li>
        </ul>

        <br />
        <h3>E. Updating, Deleting, and Correcting Your Personal Information</h3>
        <p>Key points to note:</p>
        <ul style={{ listStyle: "disc", paddingLeft: "20px" }}>
          <li>
            You are responsible for keeping your data accurate and updated.
          </li>
          <li>
            Upon request, we may deactivate your account and restrict
            visibility.
          </li>
          <li>
            We may retain closed account records for compliance and fraud
            prevention.
          </li>
          <li>
            Content you post (e.g., comments, feedback) may not be removable
            after posting.
          </li>
        </ul>
        <br />
        <h3>F. Use of AppThing Technologies Information</h3>
        <p>
          Our Website allows you to share certain personal information with{}
          <strong>AppThing Technologies</strong> in connection with exploring,
          negotiating, or utilizing our Services. If you choose to enter into a
          service agreement, you may be asked to provide personal details such
          as your name, email address, phone number, or residential address.
          These details are collected solely for enabling service delivery and
          communication.
        </p>
        <p>
          Your account password is the primary safeguard for your profile and
          data. We strongly recommend creating a password that combines numbers,
          letters, special symbols, and variations for enhanced protection.
          Never disclose your password to anyone. If you share your login
          credentials or personal details with others, you are solely
          responsible for the actions taken under your account.
        </p>
        <p>
          In the event your password is compromised, you must immediately inform{" "}
          <strong>AppThing Technologies</strong> so we can assist in resetting
          it. Any delay may lead to loss of control over your personal
          information and may expose you to unauthorized or legally binding
          actions carried out through your account.
        </p>

        <br />
        <h3>G. User-Generated Content</h3>
        <p>
          We welcome you to share content on our Website, including but not
          limited to comments, images, feedback, or other information you wish
          to publish. Please be aware that once submitted, this content becomes
          visible to the general public and can be accessed, shared, or used by
          others.
        </p>
        <p>
          By posting content, you acknowledge that{" "}
          <strong>AppThing Technologies</strong> cannot prevent third parties
          from reusing or distributing such information in ways that may
          conflict with this Privacy Policy, applicable laws, or your privacy
          rights. We encourage you to carefully consider the nature of the
          information you choose to share publicly.
        </p>

        <br />
        <h3>H. Updates to This Privacy Policy</h3>
        <p>
          This Privacy Policy is effective as of the date specified above.{" "}
          <strong>AppThing Technologies</strong> reserves the right, at its sole
          discretion, to update or amend this policy at any time. When revisions
          are made, the updated version will be posted on this page for your
          review.
        </p>
        <p>
          We encourage you to revisit this policy periodically to stay informed
          about how your information is protected and handled. Your continued
          use of our Website and Services after any updates signifies your
          acceptance of the revised policy.
        </p>
      </div>
    </>
  );
};

export default PrivacyPolicy;
